import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SelectCrmSystemCombobox } from "@/features/crm_system/components/atoms/SelectCrmSystemCombobox";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { merge } from "lodash";
import { User, Users } from "lucide-react";
import { forwardRef, useCallback, useImperativeHandle, useMemo } from "react";
import { useForm } from "react-hook-form";
import { ORGANIZATION_FORM_DEFAULT_VALUES, OrganizationFormProps, OrganizationFormRef, organizationFormSchema, OrganizationFormSchemaType } from ".";
import { BorderBox } from "@/components/atoms/BorderBox";
import { confirmDialog } from "@/components/confirm-dialog";
import { useOrganizationCrmConnectionsItems } from "@/features/organization/hooks/useQuery/useOrganizationCrmConnectionsItems/useOrganizationCrmConnectionsItems";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";



/**
 * To use this component, you need to pass ref to it from the parent component:
 * <CreateOrganizationForm ref={formRef} ... />.
 * Use the ref to access the form methods. For example:
 * const formRef = useRef<CreateOrganizationFormRef>(null);
 * const form = createOrganizationFormRef.current?.form;.
 * Then you can access methods like this: form?.handleSubmit
 */
export const OrganizationForm = forwardRef<OrganizationFormRef, OrganizationFormProps>(({
    defaultValues = ORGANIZATION_FORM_DEFAULT_VALUES,
    prohibitCrmConnectionsChange = false,
    orgId
}, ref) => {
    const defaultValuesExtended = useMemo(() => (merge({}, ORGANIZATION_FORM_DEFAULT_VALUES, defaultValues)), [defaultValues])

    const form = useForm<OrganizationFormSchemaType>({
        resolver: zodResolver(organizationFormSchema),
        defaultValues: defaultValuesExtended,
    })
    const resetForm = useCallback(() => {
        form.reset({
            ...defaultValuesExtended,
        })
    }, [defaultValuesExtended, form])


    useImperativeHandle(ref, () => ({
        form,
        resetForm,
    }), [form, resetForm])

    const { data: crms, isLoading: isCrmLoading } = useOrganizationCrmConnectionsItems({
        orgId: orgId,
        enabled: prohibitCrmConnectionsChange,
    })
    return (
        <Form {...form}>
            <form className="flex flex-col gap-4">
                <FormField
                    name="name"
                    control={form.control}
                    render={({ field }) => (
                        <FormItem className="mb-4">
                            <FormLabel>Organization Name</FormLabel>
                            <FormControl>
                                <Input {...field} placeholder="Enter organization name" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    name="crm_connections"
                    control={form.control}
                    render={({ field }) => (
                        <FormItem className="mb-4">
                            <FormLabel>CRM Connections</FormLabel>
                            <FormControl>
                                <SelectCrmSystemCombobox
                                    selectedValue={field.value}
                                    setSelectedValue={(value: string | string[] | null) => {
                                        if (prohibitCrmConnectionsChange && (
                                            defaultValuesExtended
                                                .crm_connections
                                                ?.some(
                                                    crm_connection => !value?.includes(crm_connection)
                                                )
                                        )) {
                                            const affectedCrmSystemId = defaultValuesExtended
                                                .crm_connections
                                                ?.find(
                                                    crm_connection => !value?.includes(crm_connection)
                                                )
                                            if (!affectedCrmSystemId) return
                                            const affectedCrmSystem = crms?.get(affectedCrmSystemId)
                                            confirmDialog({
                                                title: "Are you sure?",
                                                description: (
                                                    <div className="">
                                                        <p>If you remove this CRM connection you will lose access to related data and functionality until you bring it back.</p>
                                                    </div>
                                                ),
                                                children: (
                                                    <div>
                                                        <Separator className="my-2" />
                                                        <p>This change will affect:</p>
                                                        <ul>
                                                            <ShouldRenderAndPluralize data={affectedCrmSystem?.crm.length} text={"Crm System"}/>
                                                            <ShouldRenderAndPluralize data={affectedCrmSystem?.crmProject.length} text={"CRM Project"}/>
                                                            <ShouldRenderAndPluralize data={affectedCrmSystem?.crmConfig.length} text={"CRM Configuration"}/>
                                                            <ShouldRenderAndPluralize data={affectedCrmSystem?.project.length} text={"Project"}/>
                                                            <ShouldRenderAndPluralize data={affectedCrmSystem?.survey.length} text={"Survey"}/>
                                                            <ShouldRenderAndPluralize data={affectedCrmSystem?.question.length} text={"Question"}/>
                                                        </ul>
                                                    </div>
                                                )
                                            }).async().then(res => {
                                                console.log("res", res)
                                                if (res.isConfirmed) {
                                                    field.onChange(value)
                                                }
                                            })
                                            return
                                        }
                                        field.onChange(value)
                                    }}
                                    mode="multiple"
                                    disabled={isCrmLoading}
                                />
                            </FormControl>
                            {isCrmLoading && (
                                <div className="flex items-center gap-2">
                                    <Spinner className="size-4"/>
                                    <p className="text-sm text-muted-foreground">Loading CRM connections data...</p>
                                </div>
                            )}
                            {/* {prohibitCrmConnectionsChange && (
                                <BorderBox variant='danger' size='sm' className="mt-2">
                                    You cannot remove CRM connections once they are added for this organization.
                                </BorderBox>
                            )} */}
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    name="organization_type"
                    control={form.control}
                    render={({ field }) => (
                        <FormItem className="mb-4">
                            <FormLabel>My organization surveys:</FormLabel>
                            <FormControl>
                                <div className="flex justify-center">
                                    <div className="grid grid-cols-2 gap-4">
                                        <Button
                                            variant='outline'
                                            className={cn('h-32 text-center flex flex-col items-center justify-center', {
                                                'bg-primary text-primary-foreground hover:bg-primary/90': field.value === 'organization'
                                            })}
                                            onClick={() => field.onChange('organization')}
                                            type="button"
                                        >
                                            <Users size={40} />
                                            <p>Groups or projects</p>
                                        </Button>
                                        <Button
                                            variant='outline'
                                            className={cn('h-32 text-center flex flex-col items-center justify-center', {
                                                'bg-primary text-primary-foreground hover:bg-primary/90': field.value === 'user'
                                            })}
                                            onClick={() => field.onChange('user')}
                                            type="button"
                                        >
                                            <User size={40} />
                                            <p>Individuals or users</p>
                                        </Button>
                                    </div>
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </form>
        </Form>
    )
})

interface ShouldRenderAndPluralizeProps {
    data?: number
    text: string
}

function ShouldRenderAndPluralize({ data, text }: Readonly<ShouldRenderAndPluralizeProps>) {
    if (!data) return null
    return (
        <li>
            <b>{data}</b> {text}{data > 1 && "s"}
        </li>
    )
}